import * as support from "./SovMetrics.support";
import InfoIcon from "@mui/icons-material/Info";
import { Box, Stack, Typography, Tooltip } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useSelector } from "react-redux";
import * as staticDataService from "services/staticDataService";
import * as stateSelectors from "store/selectors/stateSelectors";

const defaultMetrics = [
  {
    key: "locationCount",
    label: "Location Count",
    value: {
      calculation: "length",
    },
    dp: 0,
  },
  {
    key: "totalTiv",
    label: "Total TIV",
    value: {
      calculation: "sum",
      field: "_TIV",
    },
    dp: 0,
    prefix: "$",
  },
  {
    key: "totalPd",
    label: "Total PD",
    value: {
      calculation: "sum",
      field: "_PD",
    },
    dp: 0,
    prefix: "$",
  },
  {
    key: "totalBi",
    label: "Total BI",
    value: {
      calculation: "sum",
      field: "_BI",
    },
    dp: 0,
    prefix: "$",
  },
];

const MiniTrendBar = ({ current, prior, color = "#1976d2" }) => {
  if (!prior || prior === 0) return null;

  const maxValue = Math.max(current, prior);
  const currentHeight = (current / maxValue) * 20;
  const priorHeight = (prior / maxValue) * 20;

  return (
    <Stack
      direction={"row"}
      alignItems={"end"}
      spacing={0.5}
      sx={{ height: "24px", ml: 1 }}
    >
      <Box
        sx={{
          width: "4px",
          height: `${priorHeight}px`,
          backgroundColor: "rgba(0,0,0,0.3)",
          borderRadius: "2px 2px 0 0",
          minHeight: "2px",
        }}
      />
      <Box
        sx={{
          width: "4px",
          height: `${currentHeight}px`,
          backgroundColor: color,
          borderRadius: "2px 2px 0 0",
          minHeight: "2px",
        }}
      />
    </Stack>
  );
};

const SovMetrics = ({ locations }) => {
  const theme = useTheme();
  const {
    data: sovConfig,
    isLoading: isConfigLoading,
  } = staticDataService.useConfigQuery("sov");

  const priorState = useSelector(stateSelectors.selectPriorState) || {};
  const priorSovSummary =
    priorState?.analytics?.programPricing?.values?.sovSummary || null;

  const metricsConfig = sovConfig?.metrics ?? defaultMetrics;

  const formatMetricValue = (metricDef, value) => {
    if (typeof value !== "number") return null;
    const dp = metricDef?.dp;
    const prefix = metricDef?.prefix || "";
    const formatted = new Intl.NumberFormat("en-US", {
      minimumFractionDigits: dp ?? 0,
      maximumFractionDigits: dp ?? 0,
    }).format(value);
    return `${prefix}${formatted}`;
  };

  const calculatePercentageChange = (current, prior) => {
    if (!prior || prior === 0) return null;
    return ((current - prior) / prior) * 100;
  };

  const getTrendColor = (percentChange) => {
    if (percentChange === null) return "#1976d2";
    if (percentChange > 0) return "#2e7d32";
    if (percentChange < 0) return "#d32f2f";
    return "#1976d2";
  };

  const metrics = isConfigLoading
    ? []
    : support.calculateMetrics({ metrics: metricsConfig, locations });

  const enhancedMetrics = metrics.map((metric, idx) => {
    const metricDef = metricsConfig?.[idx];
    const isLocationCount = metricDef?.key === "locationCount";
    const isTiv = metricDef?.key === "totalTiv";
    const isPd = metricDef?.key === "totalPd";
    const isBi = metricDef?.key === "totalBi";
    const isAle = metricDef?.key === "totalAle";

    let priorValue = null;
    if (priorSovSummary) {
      if (isLocationCount) priorValue = priorSovSummary?.locationCount;
      if (isTiv) priorValue = priorSovSummary?.totalTiv;
      if (isPd) priorValue = priorSovSummary?.totalPd;
      if (isBi) priorValue = priorSovSummary?.totalBi;
      if (isAle) priorValue = priorSovSummary?.totalAle;
    }

    if (
      (isLocationCount || isTiv || isPd || isBi || isAle) &&
      typeof priorValue === "number"
    ) {
      const priorFormatted = formatMetricValue(metricDef, priorValue);
      const percentChange = calculatePercentageChange(metric.value, priorValue);

      return {
        ...metric,
        label: metric.label,
        priorValue: priorFormatted,
        priorValueRaw: priorValue,
        percentChange,
        trendColor: getTrendColor(percentChange),
        tip: `Current: ${formatMetricValue(
          metricDef,
          metric.value
        )} | Last Year: ${priorFormatted}${
          percentChange !== null
            ? ` (${percentChange > 0 ? "+" : ""}${percentChange.toFixed(1)}%)`
            : ""
        }`,
      };
    }
    return metric;
  });

  return (
    <Box
      sx={{
        display: "inline-block",
        margin: "0.5rem 1rem",
        padding: "0.875rem 0.375rem",
        background: theme.brand.gray[500],
        borderRadius: "0.625rem",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-evenly",
          flexDirection: "row",
          flexWrap: "wrap",
        }}
      >
        {enhancedMetrics.map((metric, index) => (
          <Stack key={index} sx={{ margin: "0.5rem", alignItems: "center" }}>
            <Stack direction={"row"} alignItems={"center"}>
              <Typography variant={"h5"} sx={{ fontWeight: 600 }}>
                {typeof metric.value === "number"
                  ? `${metric.prefix || ""}${
                      metric.dp != null
                        ? new Intl.NumberFormat("en-US", {
                            minimumFractionDigits: metric.dp ?? 0,
                            maximumFractionDigits: metric.dp ?? 0,
                          }).format(metric.value)
                        : metric.value.toLocaleString()
                    }`
                  : metric.value}
              </Typography>

              <MiniTrendBar
                current={metric.value}
                prior={metric.priorValueRaw}
                color={metric.trendColor}
              />
            </Stack>

            {metric.priorValue && (
              <Stack
                direction={"row"}
                alignItems={"center"}
                spacing={1}
                sx={{ mt: 0.5 }}
              >
                <Typography
                  variant={"caption"}
                  sx={{
                    color: "text.secondary",
                    fontSize: "0.7rem",
                  }}
                >
                  {"LY: "}
                  {metric.priorValue}
                </Typography>

                {metric.percentChange !== null && (
                  <Typography
                    variant={"caption"}
                    sx={{
                      color: metric.trendColor,
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      backgroundColor: "rgba(255,255,255,0.7)",
                      padding: "1px 4px",
                      borderRadius: "3px",
                    }}
                  >
                    {metric.percentChange > 0 ? "+" : ""}
                    {metric.percentChange.toFixed(1)}
                    {"%"}
                  </Typography>
                )}
              </Stack>
            )}

            <Stack direction={"row"} alignItems={"center"} mt={0.5}>
              <Typography variant={"body2"} sx={{ textAlign: "center" }}>
                {metric.label}
              </Typography>
              {metric.tip && (
                <Tooltip title={metric.tip}>
                  <InfoIcon
                    sx={{
                      fontSize: "0.9375rem",
                      marginLeft: "0.25rem",
                      fill: theme.brand.gray.muted,
                    }}
                  />
                </Tooltip>
              )}
            </Stack>
          </Stack>
        ))}
      </Box>
    </Box>
  );
};

export default SovMetrics;
